"use client";

import type { ComponentProps, RefObject } from "react";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";

import { badgeVariants } from "~/components/ui/badge";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";
import { cn } from "~/lib/utils";

export type InlineCitationProps = ComponentProps<"span">;

export const InlineCitation = ({
    className,
    ...props
}: InlineCitationProps) => (
    <span
        className={cn("group inline-flex items-center gap-1", className)}
        {...props}
    />
);

export type InlineCitationTextProps = ComponentProps<"span">;

export const InlineCitationText = ({
    className,
    ...props
}: InlineCitationTextProps) => (
    <span
        className={cn("transition-colors group-hover:bg-accent", className)}
        {...props}
    />
);

const HOVER_OPEN_DELAY_MS = 100;
const HOVER_CLOSE_DELAY_MS = 150;

type InlineCitationCardContextValue = {
    scheduleOpen: () => void;
    scheduleClose: () => void;
    cancelClose: () => void;
    contentRef: RefObject<HTMLDivElement | null>;
    handleOpenAutoFocus: (event: Event) => void;
    handleCloseAutoFocus: (event: Event) => void;
};

const InlineCitationCardContext =
    createContext<InlineCitationCardContextValue | null>(null);

export type InlineCitationCardProps = ComponentProps<typeof Popover>;

export const InlineCitationCard = (props: InlineCitationCardProps) => {
    const [open, setOpen] = useState(false);
    const openRef = useRef(open);
    openRef.current = open;
    // Whether the current open state came from hover/focus timers (vs an
    // explicit click/Enter through Radix) — hover opens must not move focus.
    const openedFromHover = useRef(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const openTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (openTimeout.current) clearTimeout(openTimeout.current);
            if (closeTimeout.current) clearTimeout(closeTimeout.current);
        },
        [],
    );

    const cancelClose = useCallback(() => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
            closeTimeout.current = null;
        }
    }, []);

    const scheduleOpen = useCallback(() => {
        cancelClose();
        openTimeout.current ??= setTimeout(() => {
            openTimeout.current = null;
            if (!openRef.current) {
                openedFromHover.current = true;
                setOpen(true);
            }
        }, HOVER_OPEN_DELAY_MS);
    }, [cancelClose]);

    const scheduleClose = useCallback(() => {
        if (openTimeout.current) {
            clearTimeout(openTimeout.current);
            openTimeout.current = null;
        }
        closeTimeout.current ??= setTimeout(() => {
            closeTimeout.current = null;
            setOpen(false);
        }, HOVER_CLOSE_DELAY_MS);
    }, []);

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (openTimeout.current) {
                clearTimeout(openTimeout.current);
                openTimeout.current = null;
            }
            cancelClose();
            if (nextOpen) openedFromHover.current = false;
            setOpen(nextOpen);
        },
        [cancelClose],
    );

    const handleOpenAutoFocus = useCallback((event: Event) => {
        if (openedFromHover.current) event.preventDefault();
    }, []);

    const handleCloseAutoFocus = useCallback((event: Event) => {
        if (openedFromHover.current) event.preventDefault();
    }, []);

    const contextValue = useMemo(
        () => ({
            scheduleOpen,
            scheduleClose,
            cancelClose,
            contentRef,
            handleOpenAutoFocus,
            handleCloseAutoFocus,
        }),
        [
            scheduleOpen,
            scheduleClose,
            cancelClose,
            handleOpenAutoFocus,
            handleCloseAutoFocus,
        ],
    );

    return (
        <InlineCitationCardContext.Provider value={contextValue}>
            <Popover open={open} onOpenChange={handleOpenChange} {...props} />
        </InlineCitationCardContext.Provider>
    );
};

export type InlineCitationCardTriggerProps = ComponentProps<"button"> & {
    sources: string[];
};

function getSourceLabel(sources: string[]): string {
    const firstSource = sources[0];
    if (!firstSource) {
        return "unknown";
    }

    try {
        const hostname = new URL(firstSource).hostname;
        return hostname || firstSource;
    } catch {
        return firstSource;
    }
}

export const InlineCitationCardTrigger = ({
    sources,
    className,
    children,
    ...props
}: InlineCitationCardTriggerProps) => {
    const hover = useContext(InlineCitationCardContext);

    return (
        <PopoverTrigger asChild>
            <button
                className={cn(
                    badgeVariants({ variant: "secondary" }),
                    "ml-1 h-5 cursor-pointer rounded-full px-1.5 text-[0.65rem] leading-none align-baseline font-normal hover:bg-secondary/80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-none",
                    className,
                )}
                type="button"
                onMouseEnter={hover?.scheduleOpen}
                onMouseLeave={hover?.scheduleClose}
                onFocus={(event) => {
                    hover?.cancelClose();
                    // Only hover-open on keyboard focus; mousedown focus from a
                    // dismissing click must not re-arm the open timer.
                    if (event.currentTarget.matches(":focus-visible")) {
                        hover?.scheduleOpen();
                    }
                }}
                onBlur={(event) => {
                    // A null relatedTarget means a click on a non-focusable
                    // element — inside the card (stay open) or outside (Radix
                    // dismisses it). Only close when focus verifiably moved
                    // somewhere outside the card, e.g. tabbing away.
                    const next = event.relatedTarget as Node | null;
                    if (!next || hover?.contentRef.current?.contains(next)) {
                        return;
                    }
                    hover?.scheduleClose();
                }}
                {...props}
            >
                {children ?? (
                    <>
                        {getSourceLabel(sources)}
                        {sources.length > 1 && ` +${sources.length - 1}`}
                    </>
                )}
            </button>
        </PopoverTrigger>
    );
};

export type InlineCitationCardBodyProps = ComponentProps<typeof PopoverContent>;

export const InlineCitationCardBody = ({
    className,
    ...props
}: InlineCitationCardBodyProps) => {
    const hover = useContext(InlineCitationCardContext);

    return (
        <PopoverContent
            ref={hover?.contentRef}
            className={cn("w-80 p-0 duration-200 ease-out", className)}
            onMouseEnter={hover?.scheduleOpen}
            onMouseLeave={hover?.scheduleClose}
            onFocusCapture={hover?.cancelClose}
            onBlurCapture={(event) => {
                const next = event.relatedTarget as Node | null;
                if (!next || event.currentTarget.contains(next)) {
                    return;
                }
                hover?.scheduleClose();
            }}
            onOpenAutoFocus={hover?.handleOpenAutoFocus}
            onCloseAutoFocus={hover?.handleCloseAutoFocus}
            {...props}
        />
    );
};

export type InlineCitationCarouselProps = ComponentProps<"div">;

export const InlineCitationCarousel = ({
    className,
    ...props
}: InlineCitationCarouselProps) => (
    <div className={cn("w-full", className)} {...props} />
);

export type InlineCitationCarouselContentProps = ComponentProps<"div">;

export const InlineCitationCarouselContent = ({
    className,
    ...props
}: InlineCitationCarouselContentProps) => (
    <div className={cn("w-full", className)} {...props} />
);

export type InlineCitationCarouselItemProps = ComponentProps<"div">;

export const InlineCitationCarouselItem = ({
    className,
    ...props
}: InlineCitationCarouselItemProps) => (
    <div className={cn("w-full space-y-2 p-4", className)} {...props} />
);

export type InlineCitationCarouselHeaderProps = ComponentProps<"div">;

export const InlineCitationCarouselHeader = ({
    className,
    ...props
}: InlineCitationCarouselHeaderProps) => (
    <div
        className={cn(
            "flex items-center justify-between gap-2 rounded-t-md bg-secondary p-2",
            className,
        )}
        {...props}
    />
);

export type InlineCitationCarouselIndexProps = ComponentProps<"div">;

export const InlineCitationCarouselIndex = ({
    className,
    children,
    ...props
}: InlineCitationCarouselIndexProps) => (
    <div
        className={cn(
            "flex flex-1 items-center justify-end px-3 py-1 text-xs text-muted-foreground",
            className,
        )}
        {...props}
    >
        {children ?? "1/1"}
    </div>
);

export type InlineCitationCarouselPrevProps = ComponentProps<"button">;

export const InlineCitationCarouselPrev = ({
    className,
    children,
    ...props
}: InlineCitationCarouselPrevProps) => (
    <button
        aria-label="Previous"
        className={cn("shrink-0", className)}
        type="button"
        {...props}
    >
        {children ?? <ArrowLeftIcon className="size-4 text-muted-foreground" />}
    </button>
);

export type InlineCitationCarouselNextProps = ComponentProps<"button">;

export const InlineCitationCarouselNext = ({
    className,
    children,
    ...props
}: InlineCitationCarouselNextProps) => (
    <button
        aria-label="Next"
        className={cn("shrink-0", className)}
        type="button"
        {...props}
    >
        {children ?? (
            <ArrowRightIcon className="size-4 text-muted-foreground" />
        )}
    </button>
);

export type InlineCitationSourceProps = ComponentProps<"div"> & {
    title?: string;
    url?: string;
    description?: string;
};

export const InlineCitationSource = ({
    title,
    url,
    description,
    className,
    children,
    ...props
}: InlineCitationSourceProps) => (
    <div className={cn("space-y-1", className)} {...props}>
        {title && (
            <h4 className="truncate text-sm leading-tight font-medium">
                {title}
            </h4>
        )}
        {url && (
            <p className="truncate break-all text-xs text-muted-foreground">
                {url}
            </p>
        )}
        {description && (
            <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                {description}
            </p>
        )}
        {children}
    </div>
);

export type InlineCitationQuoteProps = ComponentProps<"blockquote">;

export const InlineCitationQuote = ({
    children,
    className,
    ...props
}: InlineCitationQuoteProps) => (
    <blockquote
        className={cn(
            "border-l-2 border-muted pl-3 text-sm text-muted-foreground italic",
            className,
        )}
        {...props}
    >
        {children}
    </blockquote>
);
